/* eslint-disable camelcase */
import {
    TRACKING_INFORMATION_ACTION_REVEAL,
    TrackType,
    TrackingErrorType,
    TrackingInformationTrigger,
    TrackingInformationUiType,
    trackIt,
} from '@vfde-sails/tracking';
import { JarvisAvailability, JarvisError, JarvisSuggestion } from '../interface';
import {
    TRACKING_CAPTCHA_SHOWN,
    TRACKING_ERROR_ACTION,
} from './constants';
import { LeadManagementTrigger, TrackingPageType, TrackingStepName, TrackingType } from './enum';
import {
    TrackingCheckOutcome,
    TrackingError,
    TrackingForm,
    TrackingInformation,
    TrackingPayload,
} from './interface';
import {
    getAvailableBandwidth,
    getAvailableProductTypes,
    getGeneralPageView,
    getHotlineNumbers,
    getPageLevels,
    getRecommendedProducts,
    formatToTrackingString,
    getProductFootPrint,
    getAvailabilityFiberProviders,
} from './util';
import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import { debugInfo } from '../util/debug';

export const trackPageView = () => {
    const payload: TrackingPayload = getGeneralPageView(TrackingPageType.Content);

    payload.hotline_numbers = getHotlineNumbers();

    trackIt(TrackType.PageView, payload);
};

export const trackJarvisError = (message: string, error: ApiErrorResponseWithCode<JarvisError>) => {
    const payload: TrackingError = {
        error_action: TRACKING_ERROR_ACTION,
        error_type: TrackingErrorType.Validation,
        error_message: formatToTrackingString(message),
        error_code: error.status.toString(),
    };

    trackIt(TrackType.Error, payload);
};

export const trackAppError = (message: string) => {
    const payload: TrackingError = {
        error_action: TRACKING_ERROR_ACTION,
        error_type: TrackingErrorType.Technical,
        error_message: formatToTrackingString(message),
        error_code: '',
    };

    trackIt(TrackType.Error, payload);
};

export const trackLeadManagement = (state: LeadManagementTrigger) => {
    const payload = {
        contact_action: state,
        contact_tool: 'lead management',
        contact_trigger: state === LeadManagementTrigger.ENGAGE ? 'automatically' : 'button',
    };

    trackIt(TrackingType.Contact, payload);
};

export const trackCaptchaShown = () => trackIt(TRACKING_CAPTCHA_SHOWN, {});

export const trackCheckStart = (isInSeamlessFlow: boolean = false) => {
    if (isInSeamlessFlow){
        return;
    }

    const payload: TrackingForm = {
        ...getGeneralPageView(TrackingPageType.Availability),
        form_step: 1,
        form_step_name: TrackingStepName.START,
        form_status: ['start'],
        page_levels: getPageLevels(TrackingStepName.START),
        form_type: 'availability',
        form_name: 'category availability check',
        line_of_customer: 'consumer',
    };

    trackIt(TrackType.PageView, payload);
};

export const trackCheckComplete = (
    primary?: JarvisSuggestion,
    secondary?: JarvisSuggestion,
    isInSeamlessFlow: boolean = false,
    availability?: JarvisAvailability
) => {
    if (isInSeamlessFlow){
        return;
    }

    const payload: TrackingForm = {
        ...getGeneralPageView(TrackingPageType.Availability),
        form_step: 2,
        form_step_name: TrackingStepName.COMPLETE,
        form_status: ['end', 'success'],
        page_levels: getPageLevels(TrackingStepName.COMPLETE),
        form_type: 'availability',
        form_name: 'category availability check',
        line_of_customer: 'consumer',
        recommended_products: getRecommendedProducts(primary, secondary),
        available_technologies: getAvailableProductTypes(primary, secondary),
        available_bandwidth: getAvailableBandwidth(primary?.product?.download, secondary?.product?.download),
        transfer_point_available: false,
        product_footprint: getProductFootPrint(primary?.product?.isUnity),
    };

    if (availability?.fiber){
        payload.availability_fiber_providers = getAvailabilityFiberProviders(primary, secondary);
    }

    debugInfo('trackCheckComplete', payload);
    trackIt(TrackType.PageView, payload);
};

export const trackCheckOutcome = (primary?: JarvisSuggestion, secondary?: JarvisSuggestion, isInSeamlessFlow: boolean = false) => {
    if (!isInSeamlessFlow){
        return;
    }

    const payload: TrackingCheckOutcome = {
        ...getGeneralPageView(TrackingPageType.Availability),
        page_levels: getPageLevels(TrackingStepName.OUTCOME),
        line_of_customer: 'consumer',
        recommended_products: getRecommendedProducts(primary, secondary),
        available_technologies: getAvailableProductTypes(primary, secondary),
        available_bandwidth: getAvailableBandwidth(primary?.product?.download, secondary?.product?.download),
        transfer_point_available: false,
        product_footprint: getProductFootPrint(primary?.product?.isUnity),
    };

    trackIt(TrackType.PageView, payload);
};

export const trackAccordion = (headline: string) => {
    const payload: TrackingInformation = {
        information_action: TRACKING_INFORMATION_ACTION_REVEAL,
        information_name: formatToTrackingString(headline),
        information_ui_type: TrackingInformationUiType.Faq,
        information_trigger: TrackingInformationTrigger.Accordion,
    };

    trackIt(TrackType.Information, payload);
};
